import React from 'react';
import LegalPage from './LegalPage';

export default function TermsOfService() {
  const content = (
    <div>
      <p className="text-gray-600 mb-6">
        <strong>Effective Date:</strong> October 1, 2025
      </p>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> By accessing or using the Platform, you agree to be bound by these Terms. 
          If you do not agree to these Terms, do not use the Platform.
        </p>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Agreement to Terms</h2>
      <p className="mb-4">
        These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
        and EQ Community INC ("EQ," "Company," "we," "us," or "our") governing your access to and use of the 
        EQ Assistant platform at https://www.eqassistant.com (the "Platform").
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Eligibility and Account Registration</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Eligibility Requirements:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Be at least 18 years of age</li>
        <li>Have legal capacity to enter into binding contracts</li>
        <li>Not be prohibited from using the Services under applicable laws</li>
        <li>Represent a legitimate business entity (for commercial use)</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Account Responsibilities:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Provide accurate, current, and complete information</li>
        <li>Maintain the confidentiality of your account credentials</li>
        <li>You are responsible for all activities under your account</li>
        <li>Notify us immediately of any unauthorized access</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Acceptable Use Policy</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">You may use the Platform to:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Search for professional contacts and companies for legitimate business purposes</li>
        <li>Generate leads for recruitment, sales, or business development</li>
        <li>Enrich contact information for your existing prospects</li>
        <li>Export data for use in your own CRM or business systems</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Prohibited Uses:</h3>
      <p className="mb-3 font-semibold text-red-700">You agree NOT to:</p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Use the Platform for any illegal purpose or in violation of any laws</li>
        <li>Circumvent usage limits, credit systems, or access controls</li>
        <li>Use automated tools (bots, scrapers, crawlers) without written permission</li>
        <li>Reverse engineer or decompile any part of the Platform</li>
        <li>Use data for spam, unsolicited communications, or bulk email campaigns</li>
        <li>Sell, license, or redistribute Platform data to third parties</li>
        <li>Scrape or systematically download large portions of the database</li>
        <li>Build a competitive product or service</li>
        <li>Violate any person's rights, including privacy or intellectual property</li>
      </ul>

      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <p className="text-sm text-red-800">
          <strong>Enforcement:</strong> Violation of this Acceptable Use Policy may result in immediate suspension 
          or termination of your account, forfeiture of unused credits without refund, and legal action.
        </p>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Subscription Plans and Payment</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Billing Terms:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Payments are processed through EQ.app payment partners</li>
        <li>Subscriptions are billed monthly or annually in advance</li>
        <li>All fees are in U.S. Dollars (USD)</li>
        <li>You authorize us to charge your payment method on file</li>
        <li>Subscriptions automatically renew unless cancelled</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Credit System:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Credits are consumed when you perform searches or enrichments</li>
        <li>Unused credits do not roll over to the next billing period</li>
        <li>No refunds for unused credits upon cancellation</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Refund Policy:</h3>
      <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
        <p className="mb-2"><strong>30-Day Money-Back Guarantee (New Subscribers Only):</strong></p>
        <ul className="list-disc pl-6 text-sm space-y-1">
          <li>First-time subscribers may request a full refund within 30 days</li>
          <li>Requires usage of less than 25% of credit allocation</li>
          <li>Refunds processed within 10 business days</li>
        </ul>
      </div>
      <p className="mb-4 text-gray-700">
        <strong>No refunds after 30 days,</strong> for partial billing periods, unused credits, or account 
        suspensions due to Terms violations.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Intellectual Property Rights</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Company Intellectual Property:</h3>
      <p className="mb-4">
        The Platform and all Content, features, and functionality are owned by EQ Community INC and protected 
        by copyright, trademark, patent, and other intellectual property laws.
      </p>
      <p className="mb-4">
        <strong>Your License:</strong> We grant you a limited, non-exclusive, non-transferable, revocable license 
        to access and use the Platform for your internal business purposes only.
      </p>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">User Content:</h3>
      <p className="mb-4">
        You retain ownership of User Content you submit (queries, job descriptions, preferences). You grant us 
        a worldwide, non-exclusive, royalty-free license to use, store, and process your User Content to provide Services.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Usage and Compliance</h2>
      <p className="mb-4">
        You agree to use the Platform and data in compliance with all applicable laws, including:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>GDPR (General Data Protection Regulation)</li>
        <li>CAN-SPAM Act</li>
        <li>TCPA (Telephone Consumer Protection Act)</li>
        <li>CASL (Canada's Anti-Spam Legislation)</li>
        <li>CCPA (California Consumer Privacy Act)</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Disclaimers and Limitations of Liability</h2>
      
      <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">DISCLAIMERS</h3>
        <p className="mb-3 uppercase text-sm font-semibold">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND 
          "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
        </p>
        <ul className="list-disc pl-6 space-y-2 text-sm">
          <li><strong>No Warranty:</strong> We do not guarantee data accuracy, completeness, or reliability</li>
          <li><strong>No Uptime Guarantee:</strong> We do not guarantee 24/7 availability or error-free operation</li>
          <li><strong>No Security Guarantee:</strong> We do not guarantee protection against unauthorized access</li>
        </ul>
      </div>

      <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">LIMITATION OF LIABILITY</h3>
        <p className="mb-3 uppercase text-sm font-semibold">
          EQ COMMUNITY LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
          OR PUNITIVE DAMAGES.
        </p>
        <p className="text-sm mb-3">
          This includes lost profits, revenue, data, or business opportunities, cost of substitute services, 
          or damages from unauthorized access or security incidents.
        </p>
        <p className="text-sm font-bold">
          AGGREGATE LIABILITY CAP: Our total liability shall not exceed the amount you paid us in the 
          12 months preceding the claim, or $100 USD, whichever is greater.
        </p>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Indemnification</h2>
      <p className="mb-4">
        You agree to indemnify, defend, and hold harmless EQ Community INC from claims arising from:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Your use or misuse of the Platform</li>
        <li>Your violation of these Terms or applicable laws</li>
        <li>Your violation of third-party rights</li>
        <li>Your User Content or actions based on Platform data</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Termination</h2>
      
      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">By You:</h3>
      <p className="mb-4">
        You may terminate your account at any time by canceling your subscription or sending a request to 
        hello@eq.community.
      </p>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">By Us:</h3>
      <p className="mb-4">
        We may suspend or terminate your account immediately for:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Violation of these Terms</li>
        <li>Fraudulent, abusive, or illegal activity</li>
        <li>Non-payment of fees</li>
        <li>At our discretion for any reason</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Effects of Termination:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li>Your license to use the Platform immediately ceases</li>
        <li>We may delete your account and data after 90 days</li>
        <li>You remain liable for all fees incurred prior to termination</li>
        <li>No refunds for partial billing periods or unused credits</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Dispute Resolution</h2>
      
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <h3 className="text-lg font-bold text-blue-900 mb-2">Binding Arbitration Agreement</h3>
        <p className="text-sm text-blue-800 mb-3">
          <strong>Please read carefully:</strong> This section requires binding arbitration of disputes.
        </p>
        <p className="text-sm text-blue-800 mb-2">
          If informal resolution fails, disputes will be resolved through binding arbitration administered 
          by the American Arbitration Association (AAA).
        </p>
        <p className="text-sm text-blue-800 font-semibold">
          <strong>Class Action Waiver:</strong> All disputes must be brought on an individual basis. 
          NO class actions, class arbitrations, or representative actions.
        </p>
      </div>

      <p className="mb-4 text-sm text-gray-600">
        <strong>Opt-Out:</strong> You may opt out of arbitration by sending written notice to 
        hello@eq.community within 30 days of accepting these Terms.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. General Provisions</h2>
      
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Modifications:</strong> We may modify these Terms at any time with notice</li>
        <li><strong>Entire Agreement:</strong> These Terms and Privacy Policy constitute the entire agreement</li>
        <li><strong>Severability:</strong> If any provision is invalid, remaining provisions remain in effect</li>
        <li><strong>Assignment:</strong> You may not assign these Terms without our consent</li>
        <li><strong>Electronic Communications:</strong> You consent to receive electronic communications</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Contact Information</h2>
      <p className="mb-4">
        For questions about these Terms, please contact:
      </p>
      <div className="bg-gray-100 rounded-lg p-4 mb-4">
        <p className="mb-2"><strong>EQ Community INC</strong></p>
        <p className="mb-1">Email: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p className="mb-1">Support: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p>Website: <a href="https://www.eqassistant.com" className="text-indigo-600 hover:underline">https://www.eqassistant.com</a></p>
      </div>

      <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mt-8">
        <p className="text-sm text-indigo-900 font-semibold">
          BY USING THE PLATFORM, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND 
          BY THESE TERMS OF SERVICE.
        </p>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-600">
        <p><strong>Version:</strong> 1.0</p>
        <p><strong>Effective Date:</strong> October 1, 2025</p>
        <p><strong>Last Updated:</strong> October 1, 2025</p>
        <p className="mt-2">Copyright © 2025 EQ Community INC. All rights reserved.</p>
      </div>
    </div>
  );

  return (
    <LegalPage
      title="Terms of Service"
      content={content}
      lastUpdated="October 1, 2025"
    />
  );
}
